import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import config from "../config";
import { normalizeMediaUrl } from "../utils/normalizeMediaUrl";
import PatientSidebarShell from "./PatientSidebarShell";

const COUNTRY_CONFIG = {
  Uganda: { code: "+256", min: 9, max: 9 },
  Kenya: { code: "+254", min: 9, max: 9 },
  USA: { code: "+1", min: 10, max: 10 },
  UK: { code: "+44", min: 10, max: 10 },
  Canada: { code: "+1", min: 10, max: 10 },
  India: { code: "+91", min: 10, max: 10 }
};

const listToInput = (value) => (Array.isArray(value) ? value.join(", ") : "");
const inputToList = (value) => {
  const parsed = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return parsed.length ? parsed : null;
};

const PatientProfileEdit = () => {
  const navigate = useNavigate();
  const patientID = localStorage.getItem("patientID");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [spokenLanguage, setSpokenLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [whatsapp, setWhatsapp] = useState("");

  const [pictureUrl, setPictureUrl] = useState("");
  const [age, setAge] = useState("");
  const [race, setRace] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [education, setEducation] = useState("");
  const [medications, setMedications] = useState("");
  const [chronicConditions, setChronicConditions] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [familyContactName, setFamilyContactName] = useState("");
  const [familyContactPhone, setFamilyContactPhone] = useState("");
  const [familyContactEmail, setFamilyContactEmail] = useState("");
  const [notesContext, setNotesContext] = useState("");
  const [notesEmotional, setNotesEmotional] = useState("");
  const [notesPhysical, setNotesPhysical] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [modalConfig, setModalConfig] = useState(null);

  useEffect(() => {
    if (!patientID) {
      setLoadError("Session expired. Please log in again.");
      setLoading(false);
      return;
    }

    axios
      .get(`${config.API_BASE_URL}${config.PATIENT.PROFILE}/${patientID}`)
      .then((res) => {
        const p = res.data || {};
        const selfRegistered = p.selfRegistered || {};

        setFirstName(p.firstName || "");
        setLastName(p.lastName || "");
        setSpokenLanguage(p.spokenLanguage || "");
        setGender(p.gender || "");
        setCountry(p.country || "");
        setCity(p.city || "");

        if (p.country && COUNTRY_CONFIG[p.country]) {
          const cfg = COUNTRY_CONFIG[p.country];
          setCountryCode(cfg.code);
          if (p.whatsapp) {
            setWhatsapp(p.whatsapp.replace(cfg.code, ""));
          }
        }

        setPictureUrl(normalizeMediaUrl(selfRegistered.pictureUrl));
        setAge(selfRegistered.age ?? "");
        setRace(selfRegistered.race || "");
        setMaritalStatus(selfRegistered.maritalStatus || "");
        setEducation(selfRegistered.education || "");
        setMedications(listToInput(selfRegistered.medications));
        setChronicConditions(listToInput(selfRegistered.chronicConditions));
        setWeightKg(selfRegistered.weightKg ?? "");
        setHeightCm(selfRegistered.heightCm ?? "");
        setFamilyContactName(selfRegistered.familyContactName || "");
        setFamilyContactPhone(selfRegistered.familyContactPhone || "");
        setFamilyContactEmail(selfRegistered.familyContactEmail || "");
        setNotesContext(selfRegistered.notesContext || "");
        setNotesEmotional(selfRegistered.notesEmotional || "");
        setNotesPhysical(selfRegistered.notesPhysical || "");
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          // New resident with no saved profile yet; form stays editable.
          return;
        }
        setLoadError("Failed to load profile. You can still fill and save.");
      })
      .finally(() => setLoading(false));
  }, [patientID]);

  const handleCountryChange = (e) => {
    const selected = e.target.value;
    setCountry(selected);
    setWhatsapp("");
    setErrors({ ...errors, country: false });

    if (COUNTRY_CONFIG[selected]) {
      setCountryCode(COUNTRY_CONFIG[selected].code);
    } else {
      setCountryCode("");
    }
  };

  const handleWhatsappChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setWhatsapp(digits);
    setErrors({ ...errors, whatsapp: false });
  };

  const handlePictureUpload = (event) => {
    const inputEl = event.target;
    const file = inputEl.files?.[0];

    if (!file || !patientID) return;

    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setModalConfig({
        type: 'error',
        title: 'Invalid Image',
        message: 'Only PNG, JPEG, and WEBP images are allowed.'
      });
      inputEl.value = "";
      return;
    }

    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setModalConfig({
        type: 'error',
        title: 'Image Too Large',
        message: 'Image size must be 5MB or less.'
      });
      inputEl.value = "";
      return;
    }

    const reader = new FileReader();
    setUploadingImage(true);

    reader.onload = async () => {
      try {
        const res = await axios.post(
          `${config.API_BASE_URL}${config.PATIENT.PROFILE_PICTURE}`,
          {
            patientID,
            imageData: reader.result
          }
        );
        setPictureUrl(normalizeMediaUrl(res.data.pictureUrl));
        setModalConfig({
          type: 'success',
          title: 'Upload Successful',
          message: 'Profile picture has been updated.'
        });
      } catch (err) {
        setModalConfig({
          type: 'error',
          title: 'Upload Failed',
          message: err.response?.data?.error || "Failed to upload profile picture."
        });
      } finally {
        setUploadingImage(false);
        inputEl.value = "";
      }
    };

    reader.onerror = () => {
      setUploadingImage(false);
      inputEl.value = "";
      setModalConfig({
        type: 'error',
        title: 'Read Error',
        message: 'Failed to read the selected image.'
      });
    };

    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!firstName) newErrors.firstName = true;
    if (!lastName) newErrors.lastName = true;
    if (!spokenLanguage) newErrors.spokenLanguage = true;
    if (!gender) newErrors.gender = true;
    if (!country) newErrors.country = true;
    if (!city) newErrors.city = true;
    if (!whatsapp) newErrors.whatsapp = true;

    const cfg = COUNTRY_CONFIG[country];
    if (country && !cfg) {
      newErrors.country = true;
    }

    if (country && cfg && (whatsapp.length < cfg.min || whatsapp.length > cfg.max)) {
      newErrors.whatsapp = true;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return false;
    }

    return true;
  };

  const saveProfile = async () => {
    if (!validateForm()) {
      return;
    }

    const fullNumber = `${countryCode}${whatsapp}`;

    try {
      await axios.post(`${config.API_BASE_URL}${config.PATIENT.PROFILE}`, {
        patientID,
        firstName,
        lastName,
        spokenLanguage,
        gender,
        country,
        city,
        whatsapp: fullNumber,
        selfRegistered: {
          pictureUrl: pictureUrl || null,
          age: age === "" ? null : Number(age),
          race: race || null,
          maritalStatus: maritalStatus || null,
          education: education || null,
          medications: inputToList(medications),
          chronicConditions: inputToList(chronicConditions),
          weightKg: weightKg === "" ? null : Number(weightKg),
          heightCm: heightCm === "" ? null : Number(heightCm),
          familyContactName: familyContactName || null,
          familyContactPhone: familyContactPhone || null,
          familyContactEmail: familyContactEmail || null,
          notesContext: notesContext || null,
          notesEmotional: notesEmotional || null,
          notesPhysical: notesPhysical || null
        }
      });

      navigate("/patientProfileViewer");
    } catch (err) {
      setModalConfig({
        type: 'error',
        title: 'Save Failed',
        message: err.response?.data?.error || "Failed to save profile."
      });
    }
  };

  if (loading) return <p style={{ padding: 40 }}>Loading profile...</p>;

  return (
    <PatientSidebarShell activeItem="profile">
      <div style={card}>
        <h2 style={title}>Edit Profile</h2>
        {loadError && <p style={warningText}>{loadError}</p>}

        <SectionTitle text="Core Profile (Required)" />
        <div style={row}>
          <Input 
            label="First Name" 
            value={firstName} 
            setValue={(val) => { setFirstName(val); setErrors({ ...errors, firstName: false }); }} 
            error={errors.firstName} 
          />
          <Input 
            label="Last Name" 
            value={lastName} 
            setValue={(val) => { setLastName(val); setErrors({ ...errors, lastName: false }); }} 
            error={errors.lastName} 
          />
        </div>

        <Input
          label="Spoken Language"
          value={spokenLanguage}
          setValue={(val) => { setSpokenLanguage(val); setErrors({ ...errors, spokenLanguage: false }); }}
          error={errors.spokenLanguage}
        />

        <div style={row}>
          <Select
            label="Gender"
            value={gender}
            onChange={(e) => { setGender(e.target.value); setErrors({ ...errors, gender: false }); }}
            error={errors.gender}
            options={[
              { label: "Select Gender", value: "" },
              { label: "Male", value: "M" },
              { label: "Female", value: "F" }
            ]}
          />

          <Select
            label="Country"
            value={country}
            onChange={handleCountryChange}
            error={errors.country}
            options={[
              { label: "Select Country", value: "" },
              ...Object.keys(COUNTRY_CONFIG).map((c) => ({
                label: c,
                value: c
              }))
            ]}
          />
        </div>

        <Input 
          label="City" 
          value={city} 
          setValue={(val) => { setCity(val); setErrors({ ...errors, city: false }); }} 
          error={errors.city} 
        />

        <div style={inputGroup}>
          <label style={{ ...labelStyle, color: errors.whatsapp ? '#dc2626' : labelStyle.color }}>WhatsApp Number</label>
          <div style={phoneRow}>
            <input style={codeInput} value={countryCode} disabled />
            <input
              style={{ ...phoneInput, borderColor: errors.whatsapp ? '#dc2626' : phoneInput.borderColor }}
              value={whatsapp}
              onChange={handleWhatsappChange}
              placeholder="Enter remaining digits"
            />
          </div>
          {errors.whatsapp && <span style={errorHint}>Please enter a valid phone number.</span>}
        </div>

        <SectionTitle text="Identity and Background (Optional)" />
        <div style={inputGroup}>
          <label style={labelStyle}>Upload Profile Picture</label>
          <input
            style={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handlePictureUpload}
          />
          {uploadingImage && <small style={helperText}>Uploading image...</small>}
        </div>
        {pictureUrl && (
          <img
            src={pictureUrl}
            alt="Profile preview"
            style={imagePreview}
          />
        )}
        <div style={row}>
          <Input label="Age" value={age} setValue={setAge} type="number" />
          <Input label="Race" value={race} setValue={setRace} />
        </div>
        <div style={row}>
          <Input
            label="Marital Status"
            value={maritalStatus}
            setValue={setMaritalStatus}
          />
          <Input label="Education" value={education} setValue={setEducation} />
        </div>

        <SectionTitle text="Health and Physical (Optional)" />
        <Input
          label="Medications (comma-separated)"
          value={medications}
          setValue={setMedications}
          placeholder="Aspirin, Vitamin D"
        />
        <Input
          label="Chronic Conditions (comma-separated)"
          value={chronicConditions}
          setValue={setChronicConditions}
          placeholder="Hypertension, Diabetes"
        />
        <div style={row}>
          <Input
            label="Weight (kg)"
            value={weightKg}
            setValue={setWeightKg}
            type="number"
          />
          <Input
            label="Height (cm)"
            value={heightCm}
            setValue={setHeightCm}
            type="number"
          />
        </div>

        <SectionTitle text="Family Contact and Notes (Optional)" />
        <div style={row}>
          <Input
            label="Family Contact Name"
            value={familyContactName}
            setValue={setFamilyContactName}
          />
          <Input
            label="Family Contact Phone"
            value={familyContactPhone}
            setValue={setFamilyContactPhone}
            placeholder="+2567..."
          />
        </div>
        <Input
          label="Family Contact Email"
          value={familyContactEmail}
          setValue={setFamilyContactEmail}
          type="email"
          placeholder="family@example.com"
        />
        <TextArea
          label="Notes (Date/Time/Day context)"
          value={notesContext}
          setValue={setNotesContext}
        />
        <TextArea
          label="Emotional Notes"
          value={notesEmotional}
          setValue={setNotesEmotional}
        />
        <TextArea
          label="Physical Notes"
          value={notesPhysical}
          setValue={setNotesPhysical}
        />

        <button style={primaryBtn} onClick={saveProfile}>
          Save Profile
        </button>
      </div>

      {/* ======================
          MODAL
      ====================== */}
      {modalConfig && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={modalConfig.type === 'error' ? errorIconWrapper : successIconWrapper}>
              {modalConfig.type === 'error' ? (
                <FaExclamationCircle style={modalIconError} />
              ) : (
                <FaCheckCircle style={modalIconSuccess} />
              )}
            </div>
            <h3 style={modalTitle}>{modalConfig.title}</h3>
            <p style={modalText}>{modalConfig.message}</p>
            <button
              style={modalConfig.type === 'error' ? confirmModalBtnError : confirmModalBtnSuccess}
              onClick={() => setModalConfig(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </PatientSidebarShell>
  );
};

export default PatientProfileEdit;

const SectionTitle = ({ text }) => <h3 style={sectionTitle}>{text}</h3>;

const Input = ({
  label,
  value,
  setValue,
  type = "text",
  placeholder = "Optional",
  error
}) => (
  <div style={inputGroup}>
    <label style={{ ...labelStyle, color: error ? '#dc2626' : labelStyle.color }}>{label}</label>
    <input
      style={{ ...input, borderColor: error ? '#dc2626' : input.borderColor }}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      type={type}
      placeholder={placeholder}
    />
    {error && <span style={errorHint}>This field is required.</span>}
  </div>
);

const TextArea = ({ label, value, setValue }) => (
  <div style={inputGroup}>
    <label style={labelStyle}>{label}</label>
    <textarea
      style={textarea}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Optional"
      rows={3}
    />
  </div>
);

const Select = ({ label, value, onChange, options, error }) => (
  <div style={inputGroup}>
    <label style={{ ...labelStyle, color: error ? '#dc2626' : labelStyle.color }}>{label}</label>
    <select 
      style={{ ...input, borderColor: error ? '#dc2626' : input.borderColor }} 
      value={value} 
      onChange={onChange}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {error && <span style={errorHint}>Please select an option.</span>}
  </div>
);

const card = {
  background: "#fff",
  padding: "32px",
  width: "100%",
  maxWidth: "760px",
  borderRadius: "14px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.12)"
};

const title = { color: "#0E5580", marginBottom: "10px" };
const sectionTitle = {
  color: "#0E5580",
  marginTop: "22px",
  marginBottom: "8px",
  fontSize: "17px"
};
const row = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px"
};

const inputGroup = {
  display: "flex",
  flexDirection: "column",
  marginBottom: "14px",
  flex: 1
};

const labelStyle = {
  fontSize: "13px",
  marginBottom: "4px",
  color: "#444"
};

const input = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  fontSize: "14px"
};

const textarea = {
  ...input,
  resize: "vertical",
  fontFamily: "inherit"
};

const phoneRow = { display: "flex", gap: "6px" };
const codeInput = {
  width: "80px",
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  background: "#f0f0f0"
};
const phoneInput = {
  flex: 1,
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #ddd"
};

const fileInput = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  fontSize: "14px",
  background: "#fff"
};

const helperText = {
  marginTop: "6px",
  color: "#555",
  fontSize: "12px"
};

const imagePreview = {
  width: "120px",
  height: "120px",
  objectFit: "cover",
  borderRadius: "10px",
  border: "1px solid #ddd",
  marginBottom: "12px"
};

const primaryBtn = {
  width: "100%",
  padding: "12px",
  background: "#0E5580",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  marginTop: "14px"
};

const warningText = {
  background: "#fff3cd",
  color: "#664d03",
  border: "1px solid #ffe69c",
  borderRadius: "8px",
  padding: "10px",
  marginBottom: "12px",
  fontSize: "13px"
};

const errorHint = {
  color: "#dc2626",
  fontSize: "12px",
  marginTop: "4px"
};

// Modal Styles
const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  animation: "fadeIn 0.2s ease-out"
};

const modalContent = {
  background: "#fff",
  borderRadius: "20px",
  padding: "32px",
  width: "90%",
  maxWidth: "400px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  animation: "slideUp 0.3s ease-out",
  textAlign: "center"
};

const errorIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#fef2f2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const successIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#f0fdf4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const modalIconError = {
  fontSize: "24px",
  color: "#ef4444"
};

const modalIconSuccess = {
  fontSize: "24px",
  color: "#10b981"
};

const modalTitle = {
  fontSize: "20px",
  fontWeight: "700",
  color: "#0f172a",
  margin: "0 0 12px 0"
};

const modalText = {
  fontSize: "15px",
  color: "#64748b",
  lineHeight: "1.5",
  margin: "0 0 24px 0"
};

const confirmModalBtnError = {
  width: "100%",
  padding: "12px",
  background: "#ef4444",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};

const confirmModalBtnSuccess = {
  width: "100%",
  padding: "12px",
  background: "#10b981",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};
